import type { SupportPartnerDto } from '@/api/control/missionImpactAPI.schemas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate } from '@/lib/support';

interface SupportNextStepsCardProps {
  /** The organization's partner, or null when none is assigned. */
  partner: SupportPartnerDto | null;
}

/**
 * Numbered steps describing what happens after a ticket is submitted. The setup step names the
 * partner when one is assigned, otherwise a Data Wrangler.
 */
export function SupportNextStepsCard({ partner }: SupportNextStepsCardProps) {
  const { t } = useLanguage();
  const steps = t.support.nextSteps;
  const items = [
    steps.aiSummary,
    steps.bugs,
    partner ? fillSupportTemplate(steps.partner, { name: partner.name ?? '' }) : steps.wrangler,
    steps.waiting,
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{steps.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {items.map((item, index) => (
            <li key={item} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 text-sm">
              <span
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold background-neutral-strong text-neutral-strong"
                aria-hidden
              >
                {index + 1}
              </span>
              <span className="pt-0.5 text-muted-foreground">{item}</span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
